// Additional polyfills
import 'custom-event-polyfill'
import 'url-polyfill'

import Vue from 'vue'
import axios from 'axios'
import numeral from 'numeral'
import App from './App'
import router from './router'
import { isAllowed } from './utils'
import { resolveApiRequest } from './utils/apiRequest'
import { buildGoogleAuthStartUrl, getGoogleAuthJWT, isGoogleProviderResetError, normalizeAuthRedirectPath } from './utils/googleAuth'
import { getOnboardingStatus } from './utils/onboardingRouting'
import VueSocialSharing from 'vue-social-sharing'
import VCalendar from 'v-calendar'
import { Sortable, MultiDrag } from 'sortablejs'

import BootstrapVue from 'bootstrap-vue'
import VueClipboard from 'vue-clipboard2'

import globals from './globals'
import Popper from 'popper.js'

// Fetch interceptor for N8N chat
import { fetchInterceptor } from './utils/fetchInterceptor.js'

import { VuePlausible } from 'vue-plausible'
Vue.use(VuePlausible, {
  domain: 'olympus.exatom.io',
  enableAutoPageviews: true,
  enableAutoOutboundTracking: false
})

import CodeDiff from 'v-code-diff'
Vue.use(CodeDiff)

import { library } from '@fortawesome/fontawesome-svg-core' // import the fontawesome core
import { faTwitter, faLinkedinIn, faShopify, faWordpress } from '@fortawesome/free-brands-svg-icons' // import specific icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome' // import font awesome icon component
library.add(faTwitter, faLinkedinIn, faShopify, faWordpress)
Vue.component('font-awesome-icon', FontAwesomeIcon)

// Required to enable animations on dropdowns/tooltips/popovers
Popper.Defaults.modifiers.computeStyle.gpuAcceleration = false

Vue.config.productionTip = false
VueClipboard.config.autoSetContainer = true

import ConsentBox from './plugins/consent-box-eureka.js'

Vue.use(BootstrapVue)
Vue.use(ConsentBox, { router })
Vue.use(VueClipboard)
Vue.use(VueSocialSharing)
Vue.use(VCalendar)

Vue.filter('capitalize', function (value) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
})
Vue.filter('capitalizeWords', function (value) {
  if (!value) return ''
  return value.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
})

import { translations } from './translations.js'
import VueI18n from 'vue-i18n'
Vue.use(VueI18n)
let locale = 'en'
if (sessionStorage.getItem('locale') && Object.keys(translations).includes(sessionStorage.getItem('locale'))) {
  locale = sessionStorage.getItem('locale')
}
const i18n = new VueI18n({
  locale,
  messages: translations,
  formatFallbackMessages: false
})

/**
 * Working with dates
 */
import VueLuxon from 'vue-luxon'
import { DateTime, Duration, Settings } from 'luxon'

// Test dates i18n
// Settings.defaultLocale = 'en-US'
// Settings.defaultLocale = 'fr'

Vue.use(VueLuxon)
Vue.filter('dateShort', input => DateTime.fromISO(input).toLocaleString(DateTime.DATE_SHORT))
Vue.filter('date', input => DateTime.fromISO(input).toLocaleString(DateTime.DATE_MED))
Vue.filter('datetime', input => DateTime.fromISO(input).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS))
Vue.filter('dateRelative', function (input, fullFormat = DateTime.DATE_MED) {
  if (Math.abs(DateTime.now().ordinal - DateTime.fromISO(input).ordinal) <= 30) {
    return DateTime.fromISO(input).toRelative()
  }
  return DateTime.fromISO(input).toLocaleString(fullFormat)
})
Vue.filter('timeSeconds', seconds => {
  const duration = Duration.fromObject({ minutes: 0, seconds }).normalize().toObject()
  let result = []
  if (duration.hours) result.push(duration.hours + 'h')
  if (duration.minutes) result.push(duration.minutes + 'm')
  result.push(Math.round(duration.seconds) + 's')
  return result.join(' ')
})
Vue.filter('timezone', input => {
  const dt = DateTime.now().setZone(input)
  return input + ' (UTC ' + dt.zone.formatOffset(dt.offset, 'short') + ')'
})

// Global RTL flag
Vue.mixin({
  data: globals
})

let axiosRequestsPending = 0
const axiosLoadingIgnorePatterns = [
  process.env.VUE_APP_OLYMPUS_SCREENSHOT_API + '/screenshot'
]

axios.interceptors.request.use(
  config => {
    if (
      (typeof config.url === 'string' && config.url.indexOf('axios-hide') !== -1) ||
      (typeof config.data === 'string' && config.data.indexOf('axios-hide') !== -1) ||
      (typeof config.data === 'object' && typeof config.data['axios-hide'] !== 'undefined')) {
      // Do not show loading indicator
      return config
    }

    axiosRequestsPending++
    if (axiosLoadingIgnorePatterns.indexOf(config.url) === -1) {
      document.body.classList.add('axios-loading')
    }
    return config
  },
  error => Promise.reject(error)
)

axios.interceptors.response.use(
  response => {
    if (--axiosRequestsPending <= 0) {
      document.body.classList.remove('axios-loading')
      axiosRequestsPending = 0
    }
    return response
  },
  error => {
    if (--axiosRequestsPending <= 0) {
      document.body.classList.remove('axios-loading')
      axiosRequestsPending = 0
    }
    return Promise.reject(error)
  }
)

Vue.prototype.$api = {
  async login ({ email, password }, rememberToken = null) {
    let suffix = ''
    if (rememberToken) {
      suffix = `?remember_token=${rememberToken}`
    }
    await axios.get(`${process.env.VUE_APP_OLYMPUS_API}/auth/jwt/token${suffix}`, {
      auth: { username: email, password: password },
      headers: { Accept: 'application/x.olympus-api.v1.1+json' }
    }).then(({ data: json }) => {
      localStorage.apiToken = json.data.jwt
    })
  },
  googleAuthRedirect (mode = 'login', redirectPath = null) {
    const url = buildGoogleAuthStartUrl(process.env.VUE_APP_OLYMPUS_API, {
      mode,
      redirectPath,
      startPath: process.env.VUE_APP_GOOGLE_AUTH_START_PATH || '/auth/google/redirect'
    })
    window.location.assign(url)
  },
  async googleAuthCallback ({ code, state = null, redirect = null }) {
    const callbackPath = (process.env.VUE_APP_GOOGLE_AUTH_CALLBACK_PATH || '/auth/google/callback').startsWith('/')
      ? (process.env.VUE_APP_GOOGLE_AUTH_CALLBACK_PATH || '/auth/google/callback')
      : `/${process.env.VUE_APP_GOOGLE_AUTH_CALLBACK_PATH}`

    const payload = { code }
    if (state) payload.state = state
    const safeRedirect = normalizeAuthRedirectPath(redirect)
    if (safeRedirect) payload.redirect = safeRedirect

    const json = (await axios.post(`${process.env.VUE_APP_OLYMPUS_API}${callbackPath}`, payload, {
      headers: { Accept: 'application/x.olympus-api.v1.1+json' }
    }).catch(e => this._catch(e))).data

    const jwt = getGoogleAuthJWT(json)
    if (jwt) {
      localStorage.apiToken = jwt
    }
    return json
  },
  async refresh () {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Refreshing login token')
    }
    await axios.get(`${process.env.VUE_APP_OLYMPUS_API}/auth/jwt/refresh`, {
      headers: { Accept: 'application/x.olympus-api.v1.1+json', Authorization: `Bearer ${localStorage.apiToken}` }
    }).then(({ data: json }) => {
      localStorage.apiToken = json.data.jwt
    }).catch(e => this._catch(e))
  },
  async getTempReadToken () {
    const now = Date.now()
    if (this._tempReadTokenCache.value && (now - this._tempReadTokenCache.timestamp) < 30000) {
      return this._tempReadTokenCache.value
    }
    const value = await this.get('/auth/jwt/token/temp-read')
    if (value) {
      this._tempReadTokenCache = { value, timestamp: Date.now() }
    }
    return value
  },
  async getTempWriteToken () {
    const now = Date.now()
    if (this._tempWriteTokenCache.value && (now - this._tempWriteTokenCache.timestamp) < 30000) {
      return this._tempWriteTokenCache.value
    }
    const value = await this.get('/auth/jwt/token/temp-write')
    if (value) {
      this._tempWriteTokenCache = { value, timestamp: Date.now() }
    }
    return value
  },
  async logout () {
    await axios.get(`${process.env.VUE_APP_OLYMPUS_API}/auth/jwt/logout`, {
      headers: { Accept: 'application/x.olympus-api.v1.1+json', Authorization: `Bearer ${localStorage.apiToken}` }
    }).catch().then(() => {
      delete localStorage.apiToken
      this.mainApp.client = null
      if (router?.currentRoute?.path === "/login") {
        router.go()
      } else {
        router.push({ path: "/login" })
      }
    })
  },
  async resetInit (email) {
    await axios.post(`${process.env.VUE_APP_OLYMPUS_API}/auth/password/email`, { email }, {
      headers: { Accept: 'application/x.olympus-api.v1.1+json' }
    }).catch(e => this._catch(e))
  },
  async resetEnd (email, token, password, src = null) {
    return (await axios.post(`${process.env.VUE_APP_OLYMPUS_API}/auth/password/reset`, { email, token, password, password_confirmation: password, src }, {
      headers: { Accept: 'application/x.olympus-api.v1.1+json' }
    }).catch(e => this._catch(e))).data
  },
  // Two-Factor Authentication endpoints
  async twoFactorSecret () {
    const json = await this.get('/auth/two-factor/secret')
    return json
  },
  async twoFactorEnable (secret, code) {
    const json = await this.post('/auth/two-factor/enable', { secret, code })
    if (json.data.jwt) {
      localStorage.apiToken = json.data.jwt
    }
    return json.data
  },
  async twoFactorDisable (code) {
    const json = await this.post('/auth/two-factor/disable', { code })
    try {
      delete localStorage.twoFactorRememberToken
    } catch (e) {
      console.error('Unable to delete localStorage.twoFactorRememberToken: ', e)
    }
    return json.data
  },
  async twoFactorVerify (code, rememberToken = null) {
    const data = { code }
    if (rememberToken) {
      data.remember_token = rememberToken
    }
    const json = await this.post('/auth/two-factor/verify', data)
    localStorage.apiToken = json.data.jwt
    return json.data
  },
  async get (uri, clientHash, axiosConfig = {}, catchErrors = true) {
    const request = axios.get(`${process.env.VUE_APP_OLYMPUS_API}${uri}`, { ...await this._config(clientHash), ...axiosConfig })
    const response = await resolveApiRequest(request, catchErrors, e => this._catch(e))

    if (axiosConfig.responseType === 'blob') {
      return response
    }
    return response.data
  },
  async post (uri, data, clientHash, axiosConfig = {}, catchErrors = true) {
    const request = axios.post(`${process.env.VUE_APP_OLYMPUS_API}${uri}`, data, { ...await this._config(clientHash), ...axiosConfig })
    const response = await resolveApiRequest(request, catchErrors, e => this._catch(e))

    if (axiosConfig.responseType === 'blob') {
      return response
    }
    return response.data
  },
  async patch (uri, data, clientHash, axiosConfig = {}, catchErrors = true) {
    const request = axios.patch(`${process.env.VUE_APP_OLYMPUS_API}${uri}`, data, { ...await this._config(clientHash), ...axiosConfig })
    const response = await resolveApiRequest(request, catchErrors, e => this._catch(e))

    return response.data
  },
  async delete (uri, clientHash, axiosConfig = {}, catchErrors = true) {
    const request = axios.delete(`${process.env.VUE_APP_OLYMPUS_API}${uri}`, { ...await this._config(clientHash), ...axiosConfig })
    const response = await resolveApiRequest(request, catchErrors, e => this._catch(e))

    return response.data
  },
  async assets (uri, data, config, catchError) {
    return (
      await axios.post(`${process.env.VUE_APP_OLYMPUS_ASSET_API}${uri}`, data, { ...await this._config(), ...config })
        .catch(e => {
          if (typeof catchError === 'boolean' && catchError === false) {
            return Promise.reject(e)
          }
          return this._catch(e)
        })
    )
  },
  async screenshot (uri, data, config, catchError) {
    return (
      await axios.post(`${process.env.VUE_APP_OLYMPUS_SCREENSHOT_API}${uri}`, data, { ...await this._config(), ...config })
        .catch(e => {
          if (typeof catchError === 'boolean' && catchError === false) {
            return Promise.reject(e)
          }
          return this._catch(e)
        })
    )
  },
  async event (uri, clientHash) {
    return (await axios.get(`${process.env.VUE_APP_OLYMPUS_EVENT_API}${uri}`, await this._config(clientHash))).data
  },
  async eventPost (uri, data, clientHash) {
    return (await axios.post(`${process.env.VUE_APP_OLYMPUS_EVENT_API}${uri}`, data, await this._config(clientHash))).data
  },
  async _config (clientHash) {
    const headers = {
      headers: {
        Accept: 'application/x.olympus-api.v1.1+json',
        Authorization: `Bearer ${await this._token()}`
      }
    }

    if (typeof clientHash !== 'undefined' && clientHash) {
      headers.headers['X-Client-Hash'] = clientHash
    }

    return headers
  },
  _vue: new Vue(),
  errors: [],
  getLastErrors (amount = 1) {
    return this.errors.slice(amount * -1)
  },
  getLastErrorsAsJSON (amount = 1) {
    return JSON.stringify(this.getLastErrors(amount))
  },
  _catch (error) {
    if (isGoogleProviderResetError(error)) {
      return Promise.reject(error)
    }

    const nonVerboseMessages = [
      'password reset token is invalid',
      'user with this email address already exists',
      'Unable to authenticate with invalid token',
      'Invalid two-factor authentication code',
      'Unauthorized login',
      'Invalid Google OAuth state'
    ]

    for (let i = 0; i < nonVerboseMessages.length; i++) {
      if (error.response && error?.response?.data?.message && error?.response?.data?.message?.indexOf(nonVerboseMessages[i]) !== -1) {
        if (error.response.data.message.indexOf('Unable to authenticate with invalid token') !== -1) {
          delete localStorage.apiToken
          this.$router.push({ path: '/login' })
        }
        return Promise.reject(error)
      }
    }

    const h = this._vue.$createElement

    console.trace({ error })
    if (error?.response?.data?.message) {
      this.errors.push({
        url: error?.config?.url,
        statusCode: error.response?.status,
        message: error.response.data.message,
        timestamp: Date.now()
      })
    }

    this._vue.$bvToast.toast([
      ...(error.response && error?.response?.data?.message ? [ error.response.data.message ] : []),
      h('div', { attrs: { class: 'text-muted mt-1' } }, error.response ? `HTTP status code: ${error.response.status}` : 'Network error'),
      h('div', { attrs: { class: 'd-flex justify-content-between' } }, [
        h('div', { attrs: { class: 'text-muted small text-monospace' } }, (new Date()).toISOString()),
        h('div', {}, [
          h('a', {
            attrs: {
              class: 'cursor-pointer text-danger small',
            },
            on: {
              click: () => this.mainApp.createTicket('User-interface error', '', true)
            }
          }, 'report')
        ]),
      ]),
    ], {
      title: [
        h('div', { class: ['d-flex', 'flex-grow-1', 'align-items-baseline'] }, [
          h('strong', { class: 'mr-auto' }, [
            h('i', { class: 'material-icons mr-1', style: 'font-size: 16px; vertical-align: sub;' }, 'error'),
            'Hmm, we didn\'t expect this'
          ])
        ])
      ],
      noAutoHide: true,
      solid: true,
      variant: 'danger'
    })

    return Promise.reject(error)
  },
  _loading: null,
  _tempReadTokenCache: { value: null, timestamp: 0 },
  _tempWriteTokenCache: { value: null, timestamp: 0 },
  async toLogin () {
    // Is this a Shopify App Install Request
    // -> Keep for later
    if (router?.currentRoute?.name === 'integrations-shopify-request') {
      localStorage.setItem('shopify_install_request', JSON.stringify({
        uri: '/' + document.location.href.split('/').slice(3).join('/'),
        timestamp: Date.now()
      }))
    }

    if (router?.currentRoute?.name !== 'login') {
      await router.push({
        path: '/login',
        query: { redirect: window.location.pathname + window.location.search }
      })
    }
    return new Promise(resolve => {})
  },
  async _token () {
    const token = this.getToken()
    if (!token) {
      return await this.toLogin()
    }

    const now = Date.now() / 1000
    const exp = this.getTokenExpiryTime()

    if (exp < now) {
      return await this.toLogin()
    }

    this.addTags()
    return localStorage.apiToken
  },
  getToken () {
    if (!localStorage.apiToken) return null
    try {
      if (JSON.parse(atob(localStorage.apiToken.split('.')[1])).exp) {
        return localStorage.apiToken
      }
    } catch (e) {
      delete localStorage.apiToken
    }
    return null
  },
  getTokenExpiryTime () {
    const token = this.getToken()
    let exp = 0
    try {
      exp = JSON.parse(atob(token.split('.')[1])).exp
    } catch (e) {}
    return exp
  },
  isLoginTokenExpired () {
    const now = Date.now() / 1000
    const exp = this.getTokenExpiryTime()
    return exp < now
  },
  async conditionallyRefreshToken () {
    const exp = this.getTokenExpiryTime()
    if (process.env.NODE_ENV !== 'production') {
      console.log('Login token remaining time ', { seconds: exp - Math.ceil(Date.now() / 1000) })
    }

    // Verify if we need to refresh the login token
    // -> if current token is not expired
    // -> only if it's going to expire in the next 15m
    if (this.isLoginTokenExpired()) return
    if ((exp - (Date.now() / 1000)) < 15 * 60) {
      this.refresh()
    }
  },
  tagsAdded: [],
  addTags (tags = []) {
    // Exatom
    if (!this.tagsAdded.includes('exatomEvent') && (!tags.length || tags.includes('exatomEvent'))) {
      try {
        const exatomEvent = document.createElement('script')
        const s = document.getElementsByTagName('script')[0]
        exatomEvent.src = 'https://assets.exatom.io/event.js?clientCode=D095'
        exatomEvent.setAttribute('nonce', '3bff553b7ffaa0b923ff173963389d5f')
        s.parentNode.insertBefore(exatomEvent, s)
      } catch (e) {}
      this.tagsAdded.push('exatomEvent')
    }

    // Exatom conversion tag
    if (!this.tagsAdded.includes('exatomConversion') && tags.includes('exatomConversion')) {
      try {
        const exatomConversion = document.createElement('script')
        const s = document.getElementsByTagName('script')[0]
        exatomConversion.src = 'https://assets.exatom.io/conversion.js?clientCode=D095&tagCode=M6WPL'
        exatomConversion.setAttribute('nonce', '3bff553b7ffaa0b923ff173963389d5f')
        s.parentNode.insertBefore(exatomConversion, s)
      } catch (e) {}
      this.tagsAdded.push('exatomConversion')
    }

    // Zoho support and knowledge base
    if (!this.tagsAdded.includes('zohoSupport') && (!tags.length || tags.includes('zohoSupport'))) {
      try {
        const zohoSupport = document.createElement('script')
        const s = document.getElementsByTagName('script')[0]
        zohoSupport.src = 'https://desk.zoho.eu/portal/api/feedbackwidget/29268000000559265?orgId=20072038995&displayType=popout'
        zohoSupport.setAttribute('nonce', '3bff553b7ffaa0b923ff173963389d5f')
        s.parentNode.insertBefore(zohoSupport, s)
      } catch (e) {}
      this.tagsAdded.push('zohoSupport')
    }
  }
}

router.onError(error => Vue.prototype.$api._catch(error))

Vue.filter('numeral', (input, format) => numeral(input).format(format))
Vue.filter('split', (input, delimiter, position = 0) => input.split(delimiter)[position])

Sortable.mount(new MultiDrag())
Vue.directive('sortable', {
    inserted: function (el, binding) {
        el.$sortable = new Sortable(el, binding.value || {})
    }
})

try {
  const currentUri = new URL(document.location.href);
  if (currentUri.searchParams.get('token')?.length > 512) {
    const token = currentUri.searchParams.get('token')

    currentUri.searchParams.delete('token'); // Change URL to not hold token
    history.replaceState({}, '', currentUri.toString())

    localStorage.apiToken = token
  }
} catch (e) {
  console.error('Error parsing token', e)
}

Vue.prototype.$api.mainApp = new Vue({
  data: {
    i18n,
    client: null,
    me: null,
    shiftPresses: 0,
    setupStatusStep: null,
    debugData: {}, // Will be shown on ALT+click the profile icon and is reset every pageview
    checkAIAnalysisCompletionInterval: null,
    checkAIAnalysisCompletionDetails: [],
  },
  methods: {
    hasRole (roleName, useInitialRoles = false) {
      if (!this.me) return false

      const roles = useInitialRoles && this.me.initialRoles ? this.me.initialRoles : this.me.roles

      if (roleName.indexOf('*') !== -1) {
        // Partial matching
        return roles.filter(role => role.name.indexOf(roleName.replace(/\*/g, '')) !== -1).length > 0
      }

      // Exact match
      return roles.map(role => role.name).includes(roleName)
    },
    clearRoles () {
      if (!this.me || this.me.roles.filter(r => r.name === 'subscription.expired').length) return
      this.me.initialRoles = this.me.roles
      this.me.roles = [{ name: 'subscription.expired' }]
    },
    resetRoles () {
      if (typeof this.me.initialRoles !== 'undefined') {
        this.me.roles = this.me.initialRoles
      }
      this.setupStatusStep = null
    },
    getCompanyProperty (name) {
      if (!this.$root?.me?.organization?.properties) return null
      return this.$root.me.organization.properties.find(p => p.type.name === name) ?? null
    },
    getWebsiteProperty (name) {
      if (!this.client?.properties) return null
      return this.client.properties.find(p => p.type.name === name) ?? null
    },
    sessionReplayIsAvailable () {
      const prop = this.$root.getWebsiteProperty('Session replays')
      return !!(prop && isAllowed(prop.value))
    },
    reload () {
      return this.$api.get('/me').then(json => {
        this.me = json.data
        this.me.initialRoles = this.me.roles

        // Keep the selected client pointed at the freshly loaded grant so changes like a
        // renamed website surface immediately instead of showing the previous (detached) copy.
        if (this.client) {
          const refreshedClient = (this.me.grants?.combined || []).find(grant => grant.client_hash === this.client.client_hash)
          if (refreshedClient) {
            this.client = refreshedClient
          }
        }

        // Restrict some environments to only admins
        if (
          ['olympus', 'olympus-prod', 'localhost', 'olympus-local'].indexOf(document.location.host.split(':')[0].split('.')[0]) === -1 &&
          !(this.hasRole('admin.internal') || this.hasRole('earlyAccess'))
        ) {
          if (document.location.host.split(':')[0].split('.')[0] === 'design') {
            const token = localStorage.apiToken
            delete localStorage.apiToken
            window.location = 'https://olympus.exatom.io/login?token=' + token
          } else {
            this.$api.logout().then(
              r => this.$router.push({ path: '/login?error=Unauthorized access to this environment.' })
            )
          }
        }

        this.me.usingGoogleAuth = this.me.properties.find(p => p.type.name === 'Google OAuth sub') !== null
      }).catch(error => {
        delete localStorage.apiToken
        this.$router.push({ path: '/login?error=Error reloading your profile, logged out.' })
      })
    },
    createTicket (subject = null, body = null, includeClientAndError = false) {
      if (!this.me || !window.zsLoadAutoSuggestions) return

      window.zsLoadAutoSuggestions()

      if ((window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) <= 850) {
        window.setTimeout(() => {
          window.closeFBSlideicon()
          window.setTimeout(() => window.slidmenuclose(), 500)
        }, 500)
      }

      window.setTimeout(() => {
        document.getElementById('feedbPopupSbmtBtn').className = 'btn btn-create'
        document.getElementById('feedbNameTxtField').value = this.me.name
        document.getElementById('feedbEmailTxtField').value = this.me.email
        if (subject && subject.length) {
          document.getElementById('feedbackSubject').value = subject
        }
        if (body && body.length) {
          document.getElementById('feedbackDescription').value = body
        }
        if (includeClientAndError) {
          document.getElementById('feedbackDescription').value += '\n\n------------------------\n\nClient: ' + this.$root.client.name + '\nClient code: ' + this.$root.client.client_code + '\n\nErrors:\n' + this.$api.getLastErrorsAsJSON()
        }
        document.getElementById('searchBox').focus()
      }, 100)
    },
    analyticsEvent (eventName, props = {}) {
      try {
        if (!this.$plausible) return
        this.$plausible.trackEvent(eventName, { props: { ...props, website: this.$root.client?.name ?? 'unknown', user: this.$root.me?.name ?? 'unknown' } })
      } catch (e) {}
    },
    async isSubscriptionExpired (clientHash = null) {
      // Not needed for eureka automation
      if (window.location.href.includes("src=grafo")) return

      if (clientHash) {
        const status = await this.getSetupStatusStep(clientHash)
        return status === 1000
      } else if (this.client) {
        const prevStatus = this.setupStatusStep
        this.setupStatusStep = await this.getSetupStatusStep(this.client.client_hash)

        // Analytics
        switch (this.setupStatusStep) {
          case 1000:
            this.$root.analyticsEvent('setupStatus', { progress: 'trial or subscripton ended' });
            break;
          case 1010:
            this.$root.analyticsEvent('setupStatus', { progress: 'Shopify -> Only show the company name setup, not the tagging' });
            break;
        }

        if (this.setupStatusStep === 1000) {
          if (this.$router.currentRoute.name !== 'account') {
            this.$router.push({ name: 'account' })
          }

          // Limit UI functions to the finance role
          if (!this.$root.hasRole('admin.internal') && !this.$root.client?.subscription_usage?.subscription_plan) {
            this.clearRoles()
          }
        } else if (prevStatus === 1000) {
          // Re-enable
          this.resetRoles()
          if (this.$router.currentRoute.name !== 'dashboard') {
            this.$router.push({ name: 'dashboard' })
          }
        }
      }
    },
    async getSetupStatusStep (clientHash) {
      const status = await getOnboardingStatus(this.$api, clientHash)
      return this.setupStepFromOnboardingStatus(status)
    },
    setupStepFromOnboardingStatus (status) {
      if (!status) return 0
      if (status.trial_expired) return 1000
      if (status.onboarding_completed) return 4
      if (!status.company_website_setup_completed) return 0
      if (status.shopify_installed) return 1010
      if (!status.tag_verified) return 2
      if (Number(status.client?.forms_count || 0) <= 0) return 3
      return 4
    },
    cl (data) {
      console.log(data)
    },
    async toggleStatus (model, id, currentStatus, clientHash = null) {
      const uri = (model.indexOf('/', 1) === -1) ? '/' + model + '/' + id : model
      const result = await this.$api.patch(
        uri,
        { status: (['ACTIVE', 'SCHEDULED'].includes(currentStatus)) ? 'INACTIVE' : (currentStatus === 'INACTIVE' ? 'ACTIVE' : currentStatus) },
        clientHash ?? this.client.client_hash
      )
      return result?.data?.status
    },
    getDomains () {
      return this.client?.domains ?? []
    },
    isValidDomain (uri) {
      if (this.getDomains().length === 0) {
        return true
      }

      if (uri.trim().match(/^https?:\/\//gi) || uri.indexOf('/') !== -1) {
        // A full uri was supplied, get domain
        uri = this.extractHostname(uri)
      }

      if (this.getDomains().includes(uri)) {
        // Exact match
        return true
      } else {
        // Partial matching, allow subdomains too
        for (const domain of this.getDomains()) {
          if (uri.trim().toLowerCase().indexOf(domain.trim().toLowerCase()) !== -1) {
            return true
          }
        }
      }
      return false
    },
    extractHostname (url) {
      let hostname
      if (url.indexOf('//') > -1) {
        hostname = url.split('/')[2]
      } else {
        hostname = url.split('/')[0]
      }

      // find & remove port number
      hostname = hostname.split(':')[0]

      // Edge cases
      // if there was a "?" but no slashes
      hostname = hostname.split('?')[0]
      // if there was a "&" but no slashes -> malformed url
      hostname = hostname.split('&')[0]
      // if there was a "#"
      hostname = hostname.split('#')[0]

      return hostname
    },
    clientCurrencyFormat (number, maximumFractionDigits = 0) {
      const options = {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0
      }
      try {
        return new Intl.NumberFormat(
          (new Intl.NumberFormat()).resolvedOptions().locale,
          {
            ...options,
            currency: this.client.country.currency_code_iso
          }).format(number)
      } catch (e) {
        return new Intl.NumberFormat(
          'nl-BE',
          {
            ...options,
            currency: 'EUR'
          }).format(number)
      }
    },
    addDebugDataToMenu (key, value) {
      this.debugData[key] = value
    },
    clearDebugDataFromMenu () {
      this.debugData = {}
    },
    getClient (clientHash) {
      const client = this.me.grants.combined.filter(grant => grant.client_hash === clientHash)
      return (client.length === 1) ? client[0] : null
    },
    async isAIAnalysisRunning (formHash) {
      if (!formHash) return false

      if (this.checkAIAnalysisCompletionDetails?.length === 0) {
        const analyses = await axios
          .get(
            `${process.env.VUE_APP_EUREKA_API}/api/analysis/${this.client.client_hash}/#axios-hide`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('apiToken')}` } }
          )

        this.checkAIAnalysisCompletionDetails = analyses
          ?.data
          ?.today
          ?.filter(a => ['PENDING', 'RUNNING'].includes(a.status));

        this.checkAIAnalysisCompletionDetails ??= []
      }

      if (this.checkAIAnalysisCompletionDetails && !this.checkAIAnalysisCompletionInterval) {
        this.checkAIAnalysisCompletionInterval = setInterval(this.checkAIAnalysisCompletion, 5000)
      }

      const data = this.checkAIAnalysisCompletionDetails?.map(a => a.strategies.map(s => s.formHash))?.flat()
      return data?.includes(formHash) ?? false
    },
    async checkAIAnalysisCompletion(analysis) {
      if (typeof analysis?.analysisId === 'string' && !this.checkAIAnalysisCompletionDetails.map(a => a.analysisId).includes(analysis.analysisId)) {
        this.checkAIAnalysisCompletionDetails.push(analysis)
        if (!this.checkAIAnalysisCompletionInterval) {
          this.checkAIAnalysisCompletionInterval = setInterval(this.checkAIAnalysisCompletion, 30000)
        }
        return
      }

      for (const analysisToComplete of this.checkAIAnalysisCompletionDetails) {
        try {
          const response = await axios.get(`${process.env.VUE_APP_EUREKA_API}/api/analysis/${this.client.client_hash}/${analysisToComplete.analysisId}#axios-hide`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('apiToken')}` } });
          const analysis = response.data;
          if (analysis.status === 'COMPLETED') {
            this.checkAIAnalysisCompletionDetails = this.checkAIAnalysisCompletionDetails.filter(a => a.analysisId !== analysis.analysisId)
            this.$emit('eureka-analysis-completed', analysis);

            const h = this.$createElement;
            const toastBody = h('div', { class: '' }, [
              h('div', { class: 'mb-3' }, [
                `Your AI Analysis “${analysis.name}” for ${analysis.clientName} is completed. `
              ]),

              // Clickable button
              h(
                'b-button',
                {
                  props: {
                    variant: 'primary',
                    size: 'sm',
                  },
                  attrs: {
                    href: `/insights/eureka/${analysis.analysisId}?client_hash=${analysis.clientHash}`
                  }
                },
                'View results'
              )
            ]);

            // 2. Show the toast
            this.$bvToast.toast(toastBody, {
              title: 'AI Analysis finished',
              solid: true,
              variant: 'success',
              noAutoHide: true
            });
          } else if (analysis.status === 'ERROR') {
            this.checkAIAnalysisCompletionDetails = this.checkAIAnalysisCompletionDetails.filter(a => a.analysisId !== analysisToComplete.analysisId)
            this.$emit('eureka-analysis-error', analysisToComplete);
            this.$bvToast.toast('Unfortunately the AI analysis of your form failed. Please try again later.', {
              title: 'AI Analysis error',
              solid: true,
              variant: 'danger',
              noAutoHide: true
            });
          } else {
            this.$emit('eureka-analysis-running', analysis);
          }
        } catch (e) {}
      }

      if (this.checkAIAnalysisCompletionDetails.length === 0) {
        clearInterval(this.checkAIAnalysisCompletionInterval)
        this.checkAIAnalysisCompletionInterval = null
      }
    },
    isShopifyInstallRequest() {
      return this.getShopifyInstallRequestUri() !== null
    },
    getShopifyInstallRequestUri() {
      try {
        let shopifyInstallRequest = JSON.parse(localStorage.getItem('shopify_install_request'))
        if (shopifyInstallRequest?.timestamp) {
          // Clean-up
          if ((Date.now() - shopifyInstallRequest.timestamp) > 3600 * 1000) {
            localStorage.removeItem('shopify_install_request')
          }
          return shopifyInstallRequest.uri
        }
        if (this?.$router?.currentRoute?.name === 'integrations-shopify-request') {
          return window.location.href
        }
      } catch (e) {}

      return null
    }
  },
  computed: {
    shouldShowSubscriptionOptions () {
      return isAllowed(this.getCompanyProperty('Subscription management')?.value)
    },
    isTrial () {
      if (!this.$root.client) return false
      return this.$root.client.subscription_usage?.trial_days_remaining !== null
    },
    trialDaysRemaining () {
      if (!this.$root.client) return null
      return this.$root.client.subscription_usage?.trial_days_remaining
    },
    trialEndDate () {
      if (!this.$root.client) return null
      return this.$root.client.subscription_usage?.trial_end_date
    },
  },
  mounted () {
    window.addEventListener('keyup', (event) => {
      if (event.key === 'Shift') {
        if (++this.shiftPresses >= 2) {
          this.$emit('shift-pressed-twice')
        } else {
          setTimeout(() => { this.shiftPresses = 0 }, 250)
        }
      }
    })

    // Check if subscription is expired
    window.setInterval(this.isSubscriptionExpired, 3600 * 1000)

    // Check if auth token is expired -> logout
    // -> The router conditionally refreshes the token per pageview
    window.setInterval(() => {
      if (this.$api.getToken() && this.$api.isLoginTokenExpired()) {
        this.$api.logout()
      }
    }, 300 * 1000)

    // Clean-up
    this.isShopifyInstallRequest()

    // N8N fetch interceptor
    const getChatMetaData = async () => {
      try {
        const baseMetadata = {
          clientId: this?.client?.client_hash,
          clientCode: this?.client?.client_code,
          isTrial: this?.isTrial === true,
          bearer: (await this.$api.getTempWriteToken()).data.jwt
        };

        // Group page-provided metadata so Eureka can inject it as one prompt context block.
        // UI-only flags stay out of the payload.
        const extra = this?.aiChatAdditionalMetadata || {};
        // eslint-disable-next-line no-unused-vars
        const { suggestionButtons, suggestionsEnabled, title, subtitle, pageType, ...rest } = extra;
        const { quickscan, formUrl, onboardingStep } = rest; // Onboarding specific data
        const data = {
          metadata: {
            ...baseMetadata,
            pageType,
            ...rest,
            additionalMetadata: {
              quickscan,
              formUrl,
              onboardingStep
            }
          }
        }
        return data
      } catch (e) {
        // Fallback: never return empty metadata; at least include client and current JWT
        const fallback = {
          metadata: {
            clientId: this?.client?.client_hash,
            isTrial: this?.isTrial === true,
            bearer: localStorage.apiToken || null
          }
        }
        return fallback
      }
    }

    // Determine chat hosts dynamically from configured webhook URLs (AI + KB)
    const hosts = []
    try {
      if (process.env.VUE_APP_AI_CHAT_WEBHOOK_URL) {
        hosts.push(new URL(process.env.VUE_APP_AI_CHAT_WEBHOOK_URL).hostname)
      }
    } catch (e) { /* noop */ }
    try {
      if (process.env.VUE_APP_KB_CHAT_WEBHOOK_URL) {
        hosts.push(new URL(process.env.VUE_APP_KB_CHAT_WEBHOOK_URL).hostname)
      }
    } catch (e) { /* noop */ }
    // Always include default host as safety net
    hosts.push('flow.exatom.io')

    // Install an interceptor per distinct host (outer wrappers pass-through to inner ones)
    Array.from(new Set(hosts.filter(Boolean))).forEach(host => {
      fetchInterceptor({ host, extraData: getChatMetaData, vueRoot: this })
    })


    // Allow the $api prototype access the app
    this.$api.mainApp = this
  },
  watch: {
    client: {
      async handler () {
        if (this.client) {
          // $root.client is a subset of $root.me.grants, that do not contain all client details (not needed)
          // However if the client is loaded/switched, dynamically inject its props
          // this.client.properties = (await this.$api.get(`/clients/${this.client.client_hash}/properties`)).data
          this.$set(this.client, 'properties', (await this.$api.get(`/clients/${this.client.client_hash}/properties`)).data)
        }
      },
      immediate: true
    }
  },
  router,
  render: h => h(App)
}).$mount('#app')
